import {
  Box,
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState, useCallback } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { useAppSelector } from "src/app/hooks";
import { useCardForm } from "src/hooks/useCardForm";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { CardField } from "src/utils/card/cardData";

interface EuroScoreFormData {
  ES_ID: string;
  PROCESS_TIME: Date | null;
  NURSE_ID: string;
  CTIME: Date;
  CUSER: string;
  AGE: string;
  AGE_SCORE: string;
  AGE_SCORE_XVALUE: string;
  GENDER_SCORE: string;
  GENDER_SCORE_XVALUE: string;
  LUNG_DISEASE: string;
  LUNG_DISEASE_XVALUE: string;
  EXTRACARDIAC: string;
  EXTRACARDIAC_XVALUE: string;
  CARDIAC_OPERATION: string;
  CARDIAC_OPERATION_XVALUE: string;
  KIDNEY_FUNCTION: string;
  KIDNEY_FUNCTION_XVALUE: string;
  KIDNEY_FAILURE: string;
  KIDNEY_FAILURE_XVALUE: string;
  ACTIVE_ENDOCARDIDE: string;
  ACTIVE_ENDOCARDIDE_XVALUE: string;
  CRITICAL_PREOPERATIVE: string;
  CRITICAL_PREOPERATIVE_XVALUE: string;
  DIABETES_MELLITUS: string;
  DIABETES_MELLITUS_XVALUE: string;
  LV_DISFUNCTION: string;
  LV_DISFUNCTION_XVALUE: string;
  PULMONARY_HYPERTENSION: string;
  PULMONARY_HYPERTENSION_XVALUE: string;
  THORACIC_AORH_SURGERY: string;
  THORACIC_AORH_SURGERY_XVALUE: string;
  POST_MI_VSD: string;
  POST_MI_VSD_XVALUE: string;
  NURSE_NOTE: string;
  RESULT: number;
}

interface EuroScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange: (field: keyof EuroScoreFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function EuroScoreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  patientData,
  isSaving,
  extraAccess,
}: EuroScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    getMCPParameterDetails,
    getScoreValue,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "EURO_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof EuroScoreFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as EuroScoreFormData;

  useEffect(() => {
    if (!isEditMode && patientData?.data && mcpCacheLoaded) {
      resetForm();
      console.log(patientData);
      const patientInfo = patientData.data;
      const birthdate = patientInfo.birthdate;
      console.log(birthdate);
      if (birthdate) {
        const birthDate = new Date(birthdate);
        const today = new Date();

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }

        const realAge = Math.max(0, age);
        console.log(realAge);

        const ageDetails = getMCPParameterDetails(120);

        let selectedAgeDetail = null;


        if (realAge <= 59) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("18") || d.name.includes("59"));
        }
        else if (realAge >= 60 && realAge <= 69) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("60"));
        }
        else if (realAge >= 70 && realAge <= 79) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("70"));
        }
        else if (realAge >= 80) {
          selectedAgeDetail = ageDetails.find(d => d.name.includes("80"));
        }

        if (selectedAgeDetail) {
          handleChange("AGE", selectedAgeDetail.value);
        } else if (ageDetails.length > 0) {
          handleChange("AGE", ageDetails[0].value);
        }
      }
    }

    else if (!isEditMode) {
      resetForm();
    }
  }, [
    isEditMode,
    resetForm,
    patientData,
    mcpCacheLoaded,
    getMCPParameterDetails,
    handleChange
  ]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      if (key !== "RESULT") {
        parentHandleChange(key as keyof EuroScoreFormData, value);
      }
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  const handleScoreCalculate = useCallback(() => {
    if (!cardData) return;

    const rowData: any = {
      AGE_SCORE_XVALUE: getScoreValue("AGE_SCORE"),
      GENDER_SCORE_XVALUE: getScoreValue("GENDER_SCORE"),
      LUNG_DISEASE_XVALUE: getScoreValue("LUNG_DISEASE"),
      EXTRACARDIAC_XVALUE: getScoreValue("EXTRACARDIAC"),
      CARDIAC_OPERATION_XVALUE: getScoreValue("CARDIAC_OPERATION"),
      KIDNEY_FUNCTION_XVALUE: getScoreValue("KIDNEY_FUNCTION"),
      KIDNEY_FAILURE_XVALUE: getScoreValue("KIDNEY_FAILURE"),
      ACTIVE_ENDOCARDIDE_XVALUE: getScoreValue("ACTIVE_ENDOCARDIDE"),
      CRITICAL_PREOPERATIVE_XVALUE: getScoreValue("CRITICAL_PREOPERATIVE"),
      DIABETES_MELLITUS_XVALUE: getScoreValue("DIABETES_MELLITUS"),
      LV_DISFUNCTION_XVALUE: getScoreValue("LV_DISFUNCTION"),
      PULMONARY_HYPERTENSION_XVALUE: getScoreValue("PULMONARY_HYPERTENSION"),
      THORACIC_AORH_SURGERY_XVALUE: getScoreValue("THORACIC_AORH_SURGERY"),
      POST_MI_VSD_XVALUE: getScoreValue("POST_MI_VSD"),
    };

    const euroScoreField = cardData.fields.find(
      (f: any) => f.field === "euro_score"
    ) as CardField | undefined;
    if (euroScoreField?.calculate) {
      const calculatedScore = euroScoreField.calculate(rowData);
      setScore(calculatedScore);
      parentHandleChange("RESULT", calculatedScore);
    }
  }, [
    cardData,
    getScoreValue,
    parentHandleChange,
  ]);

  useEffect(() => {
    handleScoreCalculate();
  }, [handleScoreCalculate]);


  const renderMCPField = (
    fieldName: string,
    labelKey: string,
    mcpId: number
  ) => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid
        xs={12}
        sm={6}
        md={6}
        sx={{ borderBottom: "1px solid #E0E0E0" }}
      >
        <ByzMuiTypography sx={{ marginLeft: "30px" }}>
          {t(labelKey)}
        </ByzMuiTypography>
      </Grid>
      <Grid xs={12} sm={6} md={6}>
        <ByzMcParameterDetailSelect
          title={t("select")}
          mcpId={mcpId}
          multiple={false}
          handleChange={(val) => {
            if (!Array.isArray(val)) {
              handleMCPChange(fieldName, val);
            }
          }}
          value={selectedMCPs[fieldName]}
          sx={{ width: "100%" }}
          cachedData={getMCPParameterDetails(mcpId)}
          cacheLoaded={mcpCacheLoaded}
          readOnly={!extraAccess}
        />
      </Grid>
    </Grid>
  );

  return (
    <>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.ES_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("euro-score.euro-score")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6} sx={{ width: "100%" }}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    }
                  }}
                  label={t("datetime")}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("euro-score.patient-related-factors")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("euro-score.age")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("euro-score.age")}
                  value={selectedMCPs.AGE_SCORE?.name || ""}
                  cantChange
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("euro-score.gender")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("euro-score.gender")}
                  value={selectedMCPs.GENDER_SCORE?.name || ""}
                  cantChange
                />
              </Grid>
            </Grid>
            {renderMCPField("LUNG_DISEASE", "euro-score.lung-disease", 302)}
            {renderMCPField("EXTRACARDIAC", "euro-score.extracardiac", 303)}
            {renderMCPField("CARDIAC_OPERATION", "euro-score.cardiac-operation", 304)}
            {renderMCPField("KIDNEY_FUNCTION", "euro-score.kidney-function", 305)}
            {renderMCPField("KIDNEY_FAILURE", "euro-score.kidney-failure", 306)}
            {renderMCPField("ACTIVE_ENDOCARDIDE", "euro-score.active-endocardide", 307)}
            {renderMCPField("CRITICAL_PREOPERATIVE", "euro-score.critical-preoperative", 308)}
            {renderMCPField("DIABETES_MELLITUS", "euro-score.diabetes-mellitus", 309)}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("euro-score.cardiac-factors")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField("LV_DISFUNCTION", "euro-score.lv-disfunction", 310)}
            {renderMCPField("PULMONARY_HYPERTENSION", "euro-score.pulmonary-hypertension", 311)}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("euro-score.operation-related-factors")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField("THORACIC_AORH_SURGERY", "euro-score.thoracic-aorta-surgery", 312)}
            {renderMCPField("POST_MI_VSD", "euro-score.post-mi-vsd", 313)}


            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("euro-score.other-factors")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("score")}
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("note")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  label={t("note")}
                  value={typedFormData.NURSE_NOTE}
                  onChange={(e) => handleChange("NURSE_NOTE", e.target.value)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default EuroScoreCreate;
